import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { chat, getStoredUser } from '../api';

const POLL_MS = 5000;
const ChatNotificationsContext = createContext(null);

function lastReadKey(uid) { return `ad_chat_last_read:${uid || 'guest'}`; }

// Soft two-tone WebAudio ding — no asset, no dependency, respects browser autoplay policy.
function playDing() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    if (ctx.state === 'suspended') {
      // Browser will resume once user interacts; silent-fail is fine
      ctx.resume().catch(() => {});
    }
    const now = ctx.currentTime;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(880, now);
    o.frequency.exponentialRampToValueAtTime(660, now + 0.18);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.05, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
    o.connect(g);
    g.connect(ctx.destination);
    o.start(now);
    o.stop(now + 0.4);
    setTimeout(() => { try { ctx.close(); } catch {} }, 500);
  } catch { /* silent — user has not interacted yet, or WebAudio blocked */ }
}

export function ChatNotificationsProvider({ children }) {
  const user = getStoredUser();
  const uid = user?.id;
  const [unread, setUnread] = useState(0);
  const [mute, setMute] = useState(() => localStorage.getItem('ad_chat_mute') === '1');
  const lastReadRef = useRef(null);
  const seenRef = useRef(new Set());
  const boot = useRef(true);

  // Initialise lastRead — never retroactively mark old history as unread
  useEffect(() => {
    if (!uid) return;
    const stored = localStorage.getItem(lastReadKey(uid));
    if (stored) {
      lastReadRef.current = stored;
    } else {
      // First time — start from "now" so old history doesn't spam the badge
      const nowIso = new Date().toISOString();
      lastReadRef.current = nowIso;
      localStorage.setItem(lastReadKey(uid), nowIso);
    }
    // eslint-disable-next-line
  }, [uid]);

  const markRead = useCallback(() => {
    if (!uid) return;
    const iso = new Date().toISOString();
    lastReadRef.current = iso;
    localStorage.setItem(lastReadKey(uid), iso);
    seenRef.current.clear();
    setUnread(0);
  }, [uid]);

  const toggleMute = useCallback(() => {
    setMute((prev) => {
      const next = !prev;
      localStorage.setItem('ad_chat_mute', next ? '1' : '0');
      return next;
    });
  }, []);

  const tick = useCallback(async () => {
    if (!uid || !localStorage.getItem('ad_token') || !lastReadRef.current) return;
    try {
      const res = await chat.list(lastReadRef.current);
      const msgs = res.messages || [];
      if (msgs.length === 0) return;
      const fresh = msgs.filter((m) => !seenRef.current.has(m.id));
      const incoming = fresh.filter((m) => m.userId !== uid);
      fresh.forEach((m) => seenRef.current.add(m.id));
      if (incoming.length > 0) {
        setUnread((u) => u + incoming.length);
        // Suppress ding on the very first poll after login (booting phase)
        if (!boot.current && !mute) playDing();
      }
      boot.current = false;
    } catch { /* transient */ }
  }, [uid, mute]);

  useEffect(() => {
    if (!uid) return;
    // Boot tick — establishes seen set silently
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => clearInterval(id);
  }, [uid, tick]);

  const value = useMemo(() => ({ unread, markRead, mute, toggleMute }), [unread, markRead, mute, toggleMute]);
  return <ChatNotificationsContext.Provider value={value}>{children}</ChatNotificationsContext.Provider>;
}

export function useChatNotifications() {
  const ctx = useContext(ChatNotificationsContext);
  return ctx || { unread: 0, markRead: () => {}, mute: false, toggleMute: () => {} };
}
